<template lang="pug">
	.radio-map.flex-col
		.map-holder
			.region-controls
				DxTextBox.region-input(ref="regionInput", :placeholder="$tCaption('Start typing to find regions..')")
				DxNumberBox.region-radius(ref="radiusInput", :format="radiusFormat", :value="radius", @value-changed="v => radius = v.value")
			.map
		dx-data-grid(
			ref="radios",
			class="dx-card wide-card"
			:data-source="dataSourceConfig"
			:focused-row-index="0"
			:show-borders="false"
			:column-auto-width="true"
			:column-hiding-enabled="true",
			:allowColumnResizing="true",
			:editing="{ allowUpdating: false, allowAdding: false, allowAdding: false, allowDeleting: false, mode: 'popup' }",
			:columns="columns",
			:noDataText="!hasData ? $tCaption('Use circle or polygon tool to select range on the map') : $tCaption('No data')",
			key-expr="station",
			@selection-changed="onRadiosSelectionChanged",
			@content-ready="onRadiosGridContentReady",
			@cellClick="onCellClick",
			:selectedRowKeys="data.radios",
			columnResizingMode="widget"
		)
			dx-paging(:page-size="10")
			dx-pager(:show-page-size-selector="true" :show-info="true")
			dx-filter-row(:visible="true")
			dx-header-filter(:visible="true")
			dx-selection(:deferred="false", mode="multiple", showCheckBoxesMode="always")
		DxPopup(
			:visible.sync="popup.visible"
			:drag-enabled="false"
			:close-on-outside-click="true"
			:show-title="false"
			:width="720"
			:height="450"
			maxWidth="90%",
			maxHeight="90%"
		)
			.radio-map--popup-content
				DxScrollView(height="100%", showScrollbar="always", ref="popup")
					.content
						.flex-row.justify.wrap(v-if="popup.data")
							.flex-row.wrap.radio-map--popup-data
								.flex-col(v-if="popup.data.logo")
									.font-small.upper.grey {{$tCaption('Logo')}}
									.half-width
										img.full-width(:src="popup.data.logo")
								.flex-col(v-if="popup.data.station")
									.font-small.upper.grey {{$tCaption('Station')}}
									.font-big {{popup.data.station}}
								.flex-col(v-if="popup.data.freq1")
									.font-small.upper.grey {{$tCaption('Frequency')}}
									.font-big {{popup.data.freq1}}
								.flex-col(v-if="popup.data.website")
									.font-small.upper.grey {{$tCaption('Website')}}
									.font-big {{popup.data.website}}
								.flex-col(v-if="popup.data.slogan")
									.font-small.upper.grey {{$tCaption('Slogan')}}
									.font-big {{popup.data.slogan}}
								.flex-col(v-if="$getSetting('custom.nielsenDma.visible') !== false")
									.font-small.upper.grey {{$getSetting('custom.nielsenDma.caption') || $tCaption('Nielsen DMA')}}
									.font-big {{popup.data.nielsen_dma}}
								.flex-col(v-if="$getSetting('custom.nielsenDmaRank.visible') !== false")
									.font-small.upper.grey {{$getSetting('custom.nielsenDmaRank.caption') || $tCaption('Nielsen DMA rank')}}
									.font-big {{popup.data.nielsen_dma_rank}}
								.flex-col(v-if="popup.data.primary_format")
									.font-small.upper.grey {{$tCaption('Primary format')}}
									.font-big {{popup.data.primary_format}}
								.flex-col(v-if="popup.data.group_owner")
									.font-small.upper.grey {{$tCaption('Group owner')}}
									.font-big {{popup.data.group_owner}}
								.flex-col(v-if="popup.data.ethnicity")
									.font-small.upper.grey {{$tCaption('Ethnicity')}}
									.font-big {{popup.data.ethnicity}}
								.flex-col(v-if="popup.data.male")
									.font-small.upper.grey {{$tCaption('% Male')}}
									.font-big {{popup.data.male}}
								.flex-col(v-if="popup.data.age")
									.font-small.upper.grey {{$tCaption('Age')}}
									.font-big {{popup.data.age}}
</template>

<script>
	import $ from 'jquery';
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import dynamic from 'dynamic';
	import api from './api.js';
	import Data from 'app-core/Data.js';
	import 'devextreme/data/odata/store';
	import DxDataGrid, {
		DxColumn,
		DxFilterRow,
		DxLookup,
		DxPager,
		DxPaging,
		DxHeaderFilter,
		DxSelection
	} from 'devextreme-vue/data-grid';
	import ArrayStore from 'devextreme/data/array_store';
	import DataSource from 'devextreme/data/data_source';
	import Promise from 'bluebird';
	import DxTextBox from 'devextreme-vue/text-box';
	import { DxNumberBox } from 'devextreme-vue/number-box';
	import { DxPopup } from 'devextreme-vue/popup';
	import DxScrollView from 'devextreme-vue/scroll-view';

	export default {
		translationCategory: 'carto.RadioMap',
		name: 'radio-map',
		components: {
			DxDataGrid,
			DxColumn,
			DxFilterRow,
			DxLookup,
			DxPager,
			DxPaging,
			DxHeaderFilter,
			DxSelection,
			DxTextBox,
			DxNumberBox,
			DxPopup,
			DxScrollView
		},
		props: {
			data: VueTypes.any.def(null),
			regions: VueTypes.array.def([]),
			mapPosition: VueTypes.object.def({})
		},
		data() {
			return {
				drawing: false,
				circleFilterData: null,
				polygonFilterData: null,
				screen(width) {
					return (width < 900) ? 'xs' : 'lg';
				},
				radius: 10,
				columns: [
					{
						dataField: 'logo',
						caption: () => this.$tCaption('Logo'),
						cssClass: 'logo-column',
						cellTemplate: (container, options) => {
							$('<div class="img-holder">')
								.append(options.value ? $('<img>', { 'class': 'logo-img', 'src': options.value }) : $('<i>', { 'class': 'fas fa-broadcast-tower' }))
								.appendTo(container);
						},
						width: 80
					},
					{ dataField: 'station', caption: () => this.$tCaption('Station'), width: 100 },
					{ dataField: 'freq1', caption: () => this.$tCaption('Frequency'), width: 110 },
					{ dataField: 'website', caption: () => this.$tCaption('Website'), width: 120 },
					{ dataField: 'slogan', caption: () => this.$tCaption('Slogan') },
					{
						dataField: 'nielsen_dma',
						caption: this.$getSetting('custom.nielsenDma.caption') || this.$tCaption('Nielsen DMA'), visible: this.$getSetting('custom.nielsenDma.visible') !== false },
					{
						dataField: 'nielsen_dma_rank',
						caption: this.$getSetting('custom.nielsenDmaRank.caption') || this.$tCaption('Nielsen DMA rank'), visible: this.$getSetting('custom.nielsenDmaRank.visible') !== false, width: 120 },
					{ dataField: 'primary_format', caption: () => this.$tCaption('Primary format') },
					{ dataField: 'group_owner', caption: () => this.$tCaption('Group owner') },
					{ dataField: 'ethnicity', caption: () => this.$tCaption('Ethnicity') },
					{ dataField: 'male', caption: () => this.$tCaption('% Male') },
					{ dataField: 'age', caption: () => this.$tCaption('Age') }
				],
				dataSourceConfig: {
					store: {
						type: 'array',
						key: 'cartodb_id',
						data: []
					}
				},
				filtersRecovered: false,
				lastSourceQuery: null,
				drawnItems: null,
				autocomplete: null,
				popup: {
					loading: false,
					visible: false,
					data: null
				}
			};
		},
		computed: {
			hasData() {
				return this.toolsSpecified || this.data.radios.length;
			},
			toolsSpecified() {
				return this.polygonFilterData || this.circleFilterData;
			},
			grid() {
				return this.$refs.radios.instance;
			},
			radiusFormat() {
				return `${this.$tCaption('Radius')}: #0 ${api.kmOrMiles()}`;
			}
		},
		watch: {
			circleFilterData() {
				this.loadStations();
			},
			polygonFilterData() {
				this.loadStations();
			},
			radius() {
				this.addPlaceFilter();
			}
		},
		mounted() {
			api.clearClient();
			window.jQuery = $;
			dynamic.loadOnce([
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.js',
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.css',
			]).then(() => {
				dynamic.loadOnce([
					'https://maps.googleapis.com/maps/api/js?key=AIzaSyBxX6xc0TryC8oGKqVLaIIyJT9w46OYsLE&libraries=places,geometry&language=en&js',
					'https://libs.cartocdn.com/carto.js/v4.2.0/carto.min.js',
					'https://carto.com/developers/carto-js/examples/maps/public/style.css',
					'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.js',
					'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.css'
				]).then(() => {
					this.buildMap();
					this.recoverFilters();
				});
			});

			this.$setQueryDebounced = _.debounce(this.setQuery, 750);
		},
		destroyed() {
			delete this.$map;
			delete this.$setQueryDebounced;
		},
		methods: {
			onRadiosSelectionChanged(v) {
				if (__DEBUG__)
					console.info('On radio selection changed', v);
				if (this.toolsSpecified)
					this.data.radios = v.selectedRowKeys;
				this.$setQueryDebounced();
			},
			recoverFilters() {
				if (this.data.radioFilters && this.data.radioFilters.length)
					this.grid.filter(this.data.radioFilters);
				this.loadStations();
				this.filtersRecovered = true;
			},
			onRadiosGridContentReady() {
				if (this.filtersRecovered) {
					var filters = this.getFilters();
					if (JSON.stringify(this.data.radioFilters) !== JSON.stringify(filters)) {
						this.data.radioFilters = filters;
						this.selectAvailableRadios().finally(() => {
							this.$setQueryDebounced();
						});
					}
				}
			},
			selectAvailableRadios() {
				if (!this.grid.totalCount()) return Promise.resolve();
				return new Promise((resolve) => {
					this.grid.clearSelection().done(() => {
						setImmediate(() => {
							if (this._isDestroyed) return;
							this.grid.selectAll().done(() => {
								resolve();
							});
						});
					});
				});
			},
			clearFilters() {
				this.drawnItems.clearLayers();
				this.circleFilterData = null;
				this.polygonFilterData = null;
			},
			getFilters() {
				var combinedFilters = this.grid.getCombinedFilter();
				var filtersArray = combinedFilters && Array.isArray(combinedFilters[0]) ? combinedFilters : [combinedFilters];
				var gridFilters = filtersArray.map(f => !Array.isArray(f) ? f : f.reduce((arr, z) =>
					[...arr, z && typeof(z) === 'function' ? this.grid.columnOption(z.columnIndex, 'dataField') : z], []));

				if (__DEBUG__)
					console.info('Filters changed', gridFilters);

				return gridFilters;
			},
			loadStations() {
				if (this.polygonFilterData || this.circleFilterData || this.data.radios.length) {
					if (__DEBUG__)
						console.log('Load stations');

					this.grid.beginCustomLoading();
					Data.getCartoSql(`
						select cartodb_id, ${this.columns.map(f => f.dataField ).join(',')}
						from (SELECT *, case band when 'FM' then freq::decimal/10 else freq end as freq1 FROM us_radio) us_radio
						${this.sqlWhere(!this.toolsSpecified)}`
					).then(r => {
						this.dataSourceConfig = new DataSource({
							store: new ArrayStore({
								key: 'station',
								data: r
							}),
						});
						this.grid.endCustomLoading();
						setImmediate(() => {
							if (this._isDestroyed) return;
							this.selectAvailableRadios();
						});
					});
				}
			},
			setQuery() {
				var query = `select the_geom, the_geom_webmercator, cartodb_id, station from us_radio ${this.sqlWhere(true)} limit 750`;
				if (__DEBUG__)
					console.log('Refresh query', { query });
				if (this.$source && query !== this.lastSourceQuery)	{
					this.$source.setQuery(query);
					this.lastSourceQuery = query;
				}
			},
			sqlWhere(filterByRadios) {
				const orFilters = [];
				const andFilters = [];
				const radios = this.data.radios && this.data.radios.length ? this.data.radios : ['do-not-show-radios'];

				if (filterByRadios)
					andFilters.push(`station in (${radios.map(f => `'${f}'`).join(',')})`);
				else {
					if (this.circleFilterData)
						orFilters.push(`
						(ST_intersects(
							the_geom,
							ST_Buffer(ST_SetSRID(ST_Point(${this.circleFilterData.lng},${this.circleFilterData.lat}),4326)::geography, ${this.circleFilterData.radius})::geometry
						))`);
					if (this.polygonFilterData)
						orFilters.push(`(ST_intersects(
							the_geom,
							ST_SetSRID(ST_GeomFromGeoJSON('${JSON.stringify(this.polygonFilterData)}'), 4326)
						))`);
					if (filterByRadios)
						orFilters.push(`station in (${radios.map(f => `'${f}'`).join(',')})`);
				}

				const where = [...(orFilters.length ? [`(${orFilters.join(' OR ')})`] : []), ...andFilters].join(' AND ');
				const result = where.length ? ` WHERE (${[where].join(' OR ')}) ` : 'where 0 = 1';

				return result;
			},
			createLayers() {
				const carto = window.carto;
				const client = this.$client;

				if (this.$layer) client.removeLayer(this.$layer);

				const style = new carto.style.CartoCSS(`
					#layer {
						polygon-fill: #f27649;
						polygon-opacity: 0.25;
					}
					#layer::outline {
						line-width: 0.5;
						line-color: #f27649;
						line-opacity: 0.1;
					}
					#layer::labels [zoom>0]{
						text-name: [station];
						text-face-name: 'DejaVu Sans Book';
						text-size: 10;
						text-fill: #000000;
						text-label-position-tolerance: 0;
						text-halo-radius: 1;
						text-halo-fill: #ffffff;
						text-dy: -10;
						text-allow-overlap: false;
						text-placement: point;
						text-placement-type: dummy;
					}
				`);

				const layer = this.$layer = new carto.layer.Layer(this.$source, style, {
					featureClickColumns: ['cartodb_id']
				});
				layer.on('featureClicked', featureEvent => {
					this.showRadioPopup(featureEvent.data.cartodb_id);
				});
				client.addLayer(layer);
			},
			addPlaceFilter() {
				var place = this.autocomplete.getPlace();
				if (place.geometry) {
					var lat = parseFloat(place.geometry.location.lat());
					var lng = parseFloat(place.geometry.location.lng());
					var radius = this.radius * api.metricMultiplier();
					this.$map.setView([lat, lng], 8);
					if (__DEBUG__)
						console.log('Place changed', { place, lat, lng, radius });
					this.clearFilters();
					window.L.circle([lat, lng], {
						radius,
						color: '#333',
						fill: false,
						opacity: 0.2
					}).addTo(this.drawnItems);
					this.circleFilterData = { lat, lng, radius };
				}
			},
			buildMap() {
				if (__DEBUG__)
					console.log('Build map carto');

				const $el = this.$el;
				const L = window.L;
				const carto = window.carto;
				const map = this.$map = L.map($(this.$el).find('.map')[0]).setView([this.mapPosition.lat || 37.8, this.mapPosition.lng || -96], this.mapPosition.zoom || 5);

				L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}@2x.png', {
					maxZoom: 18
				}).addTo(map);

				const client = this.$client = api.getClient();

				this.autocomplete = new google.maps.places.Autocomplete(
					($($el).find('.region-input input')[0])
				);
				this.autocomplete.addListener('place_changed', this.addPlaceFilter);
				$($el).find('.region-input input').attr('placeholder', ' ');

				const source = this.$source = new carto.source.SQL('select * from us_radio');
				this.setQuery();
				//source.setQuery(api.buildQuery(this.regions, this.queryFields, void 0, { where: this.sqlWhere() }));

				this.createLayers();
				this.$currentLayer = client.getLeafletLayer();
				this.$currentLayer.addTo(map);

				//polygon filter

				// create the polygon filter and add it to the dataviews
				let polygonFilter;
				function createAndBindPolygonFilter() {
					polygonFilter = new carto.filter.Polygon();
					polygonFilter.setPolygon({
						type: 'Polygon',
						coordinates: []
					});
				}

				this.drawnItems = L.featureGroup().addTo(map);

				var preparePolygonDrawing = () => {
					// layer to draw polygons
					const drawnItems = this.drawnItems;

					// Control to draw a Polygon and use it as the spatial filter
					let drawControl = new L.Control.Draw({
						draw: {
							polygon: {
								allowIntersection: false,
								showArea: true,
								shapeOptions: {
									color: '#333',
									fill: false,
									opacity: 0.2
								}
							},
							polyline: false,
							line: false,
							marker: false,
							rectangle: false,
							circle: false,
							circlemarker: false,
						},
						edit: false
					});
					map.addControl(drawControl);

					// Get radius and center & apply to Polygon filter
					map.on(L.Draw.Event.CREATED, (e, d) => {
						if (__DEBUG__)
							console.log('Set polygon filter', {e, args: arguments});
						if (e.layerType === 'polygon') {
							this.clearFilters();

							let drawnPolygon = e.layer;
							drawnItems.clearLayers();
							drawnItems.addLayer(drawnPolygon);

							// get polygon data
							let polygonData = drawnPolygon.toGeoJSON()['geometry'];
							polygonFilter.setPolygon(polygonData); // updated filter !*/

							this.polygonFilterData = polygonData;
						}
					});

				};

				createAndBindPolygonFilter();
				preparePolygonDrawing();

				this.createCircleFilter();
			},
			createCircleFilter() {
				let circleFilter = null;
				const carto = window.carto;
				const L = window.L;
				const map = this.$map;

				// create the circle filter and add it to the dataviews
				function createAndBindCircleFilter() {
					circleFilter = new carto.filter.Circle();
					circleFilter.setCircle({
						lat: 0,
						lng: 0,
						radius: 0
					});
				}

				var prepareCircleDrawing = () => {
					// layer to draw circles
					const drawnItems = this.drawnItems;

					// Control to draw a Circle and use it as the spatial filter
					let drawControl = new L.Control.Draw({
						draw: {
							polygon: false,
							polyline: false,
							line: false,
							marker: false,
							rectangle: false,
							circle: {
								shapeOptions: {
									color: '#333',
									fill: false,
									opacity: 0.2
								}
							},
							circlemarker: false,
						},
						edit: false
					});
					map.addControl(drawControl);

					map.on(L.Draw.Event.DRAWSTART, (e, d) => {
						this.drawing = true;
					});

					map.on(L.Draw.Event.DRAWSTOP, (e, d) => {
						//this.drawing = true;
						setTimeout(() => {
							if (this._isDestroyed) return;
							this.drawing = false;
						}, 500);
					});

					// Get radius and center & apply to Circle filter
					map.on(L.Draw.Event.CREATED, (e, d) => {
						if (__DEBUG__)
							console.info('Circle drawn', { e, args: arguments });
						if (e.layerType === 'circle') {
							this.clearFilters();

							let drawnCircle = e.layer;
							drawnItems.clearLayers();
							drawnItems.addLayer(drawnCircle);

							// get circle data
							let radius = drawnCircle.getRadius();
							let centerLat = drawnCircle.getLatLng().lat;
							let centerLng = drawnCircle.getLatLng().lng;

							const circleData = {
								lat: centerLat,
								lng: centerLng,
								radius: radius
							};

							circleFilter.setCircle(circleData); // updated filter !
							drawControl._toolbars.draw.disable();

							this.circleFilterData = circleData;
						}
					});
				};

				createAndBindCircleFilter();
				prepareCircleDrawing();
			},
			showRadioPopup(cartoDbId) {
				this.popup.data = this.dataSourceConfig._store._array.filter(f => f.cartodb_id === cartoDbId)[0];
				this.popup.visible = true;
				if (__DEBUG__)
					console.info('Show radio details', { cartoDbId, popup: this.popup });
			},
			onCellClick(e) {
				if (e.rowType === 'data' && e.column.type !== 'selection') {
					e.event.stopPropagation();
					e.event.preventDefault();
					this.showRadioPopup(e.data.cartodb_id);
				}
			}
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.dx-scrollable-scrollbar {
		z-index: 1000;
	}

	.radio-map--popup-content {
		height: 100%;

		.widget {
			background: #3333330f;
			padding: 10px;
			width: 236px;

			.header {
				color: #000000;
			}

			.data, .data-statistic {
				color: $base-accent;
				font-size: 30px;
				font-weight: 100;
			}
		}
	}

	.radio-map {
		.map-holder {
			height: calc(100vh - 513px);
			width: 100%;
			max-height: 65vh;
			min-height: 240px;
			margin-top: 0px;
			position: relative;

			.map {
				width: 100%;
				height: 100%;
			}

			> .proposal-buttons-group {
				position: absolute;
				left: 10px;
				bottom: 10px;
			}

			> .region-controls {
				padding: 15px 15px 15px 14px;
				background: rgba(255, 255, 255, 0.95);
				border-radius: 4px;
				position: absolute;
				top: 6px;
				left: 6px;
				width: 500px;
				max-width: 80%;
				z-index: 1000;
				box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
				display: flex;
				flex-direction: row;
				margin-left: 50px;

				> .region-input {
					flex: 5;
					margin-right: 10px;

					&.dx-texteditor.dx-editor-underlined:after {
						border-bottom: 1px solid rgba(70, 70, 70, 0.13);
					}
				}

				.region-radius {
					flex: 2;
				}
			}
		}

		.logo-column {
			padding: 0px 4px 0px 14px !important;
			text-align: center !important;
			line-height: 0px !important;

			.img-holder {
				text-align: center;
				font-size: 20px;
				color: #f276494a;
			}

			.logo-img {
				max-height: 30px;
				border-radius: 10px;
			}
		}
	}

	.radio-map--popup-data {
		> .flex-col {
			flex-basis: 50%;
			margin-top: 10px;
		}
	}
</style>
